import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FormModal from "../components/FormModal";
import { Loading } from "../components/Loader";
import { createRole, fetchRoles, selectRoles } from "../features/access/accessSlice";
import { selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";
import "./ManagementPages.css";

const EMPTY_FORM = { label: "", name: "", description: "", copy_from: "" };

/**
 * «Управление → Роли и права»: список ролей. Роль = группа Django; клик открывает матрицу прав.
 */
export default function RolesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(selectAuth);
  const roles = useSelector(selectRoles);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const closeModal = useCallback(() => {
    setCreating(false);
    setForm(EMPTY_FORM);
    setFormError(null);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    const body = {
      label: form.label.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      ...(form.copy_from ? { copy_from: Number(form.copy_from) } : {}),
    };
    const result = await dispatch(createRole(body));
    setSaving(false);
    if (createRole.fulfilled.match(result)) {
      closeModal();
      navigate(`/management/roles/${result.payload.id}`);
    } else {
      setFormError(result.payload);
    }
  };

  const list = Array.isArray(roles.data) ? roles.data : [];

  return (
    <div className="mgmt-page">
      <div className="mg-header">
        <div>
          <div className="mg-title">Роли и права</div>
          <div className="mg-subtitle">
            Роль определяет, какие разделы сотрудник видит и что может в них делать. Откройте роль, чтобы
            настроить права; роли назначаются в разделе «Пользователи».
          </div>
        </div>
        {can(user, "role", "create") && (
          <div className="mg-actions">
            <button type="button" className="mg-btn mg-btn--primary" onClick={() => setCreating(true)}>
              + Новая роль
            </button>
          </div>
        )}
      </div>

      <div className="mg-body">
        {roles.status === "loading" && !list.length && <Loading text="Загрузка ролей…" />}
        {roles.status === "failed" && <div className="mg-error">{roles.error}</div>}

        {list.length > 0 && (
          <div className="mg-table-wrap">
            <table className="mg-table">
              <thead>
                <tr>
                  <th>Роль</th>
                  <th className="mg-hide-mobile">Код</th>
                  <th>Сотрудников</th>
                  <th className="mg-hide-mobile">Описание</th>
                </tr>
              </thead>
              <tbody>
                {list.map((role) => (
                  <tr
                    key={role.id}
                    className="mg-row"
                    onClick={() => navigate(`/management/roles/${role.id}`)}
                    title="Открыть права роли"
                  >
                    <td>
                      <div className="mg-badges">
                        <strong>{role.label}</strong>
                        {role.is_system && <span className="mg-badge">системная</span>}
                      </div>
                    </td>
                    <td className="mg-mono mg-muted mg-hide-mobile">{role.name}</td>
                    <td>{role.user_count}</td>
                    <td className="mg-muted mg-hide-mobile">{role.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FormModal open={creating} title="Новая роль" onClose={closeModal}>
        <form className="mg-form" onSubmit={submit}>
          <label className="mg-field mg-field--wide">
            <span className="mg-label">Название</span>
            <input
              className="mg-input"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Бухгалтер"
              required
              autoFocus
            />
          </label>
          <label className="mg-field mg-field--wide">
            <span className="mg-label">Код</span>
            <input
              className="mg-input mg-mono"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
              placeholder="accountant"
              pattern="[a-z][a-z0-9_]*"
              required
            />
            <span className="mg-field-hint">
              Латиница, цифры и «_». Код уходит в кассовый терминал и после создания не меняется.
            </span>
          </label>
          <label className="mg-field mg-field--wide">
            <span className="mg-label">Описание</span>
            <textarea
              className="mg-textarea"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Что делает сотрудник с этой ролью"
            />
          </label>
          <label className="mg-field mg-field--wide">
            <span className="mg-label">Скопировать права из роли</span>
            <select
              className="mg-select"
              value={form.copy_from}
              onChange={(e) => setForm({ ...form, copy_from: e.target.value })}
            >
              <option value="">— начать с пустой матрицы —</option>
              {list.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          {formError && <div className="mg-error mg-field--wide">{formError}</div>}
          <div className="mg-form-actions mg-field--wide">
            <button type="button" className="mg-btn" onClick={closeModal}>
              Отмена
            </button>
            <button type="submit" className="mg-btn mg-btn--primary" disabled={saving}>
              {saving ? "Создаём…" : "Создать и настроить права"}
            </button>
          </div>
        </form>
      </FormModal>
    </div>
  );
}
